// CreateBusinessForm — US-105 + US-119.
//
// New-business modal/page form. Two fields:
//   - Name (required, ≥1 ≤120 chars)
//   - LinkedIn account (required — US-119 gap-design)
//
// US-119 UX rule (gap-design): when the user has exactly one connected
// LinkedIn account, the picker is pre-selected (the user can still change
// it before submit). When zero accounts are connected we surface the
// `accountNoneTitle` empty state and disable submit. With ≥2 accounts the
// picker is `<Select>` + label + helper text.

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { useCreateBusiness } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, FieldAssembly, Input, Select } from '@barel/ui';

import { runMutationWithToast } from '@/lib/mutationToast';

import { useLinkedinAccountsList } from './lib/useLinkedinAccountsList';

const createSchema = z.object({
  name: z.string().trim().min(1).max(120),
  linkedinAccountId: z.string().trim().min(1),
});

type CreateValues = z.infer<typeof createSchema>;

export interface CreateBusinessFormProps {
  /** Called after the create succeeds, with the new business id. */
  onCreated: (id: string) => void;
  /** Called when the user cancels the form. */
  onCancel?: () => void;
}

export function CreateBusinessForm({ onCreated, onCancel }: CreateBusinessFormProps) {
  const t = useT('businesses');
  const accounts = useLinkedinAccountsList();
  const create = useCreateBusiness();
  const onlyAccountId = accounts.data.length === 1 ? accounts.data[0]?.id : undefined;

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    mode: 'onSubmit',
    // The list page normally warms this query before the modal opens. Seed
    // the form synchronously so Radix can resolve the selected item's label
    // on its first render; the effect below handles a later query response.
    defaultValues: { name: '', linkedinAccountId: onlyAccountId ?? '' },
  });

  // US-119 UX: pre-select the only available account so single-account
  // users skip a click. Re-runs when the accounts list resolves.
  useEffect(() => {
    if (onlyAccountId && !form.getValues('linkedinAccountId')) {
      form.setValue('linkedinAccountId', onlyAccountId, {
        shouldValidate: form.formState.isSubmitted,
      });
    }
  }, [form, form.formState.isSubmitted, onlyAccountId]);

  const accountValue = form.watch('linkedinAccountId');

  const onSubmit: SubmitHandler<CreateValues> = (values) =>
    runMutationWithToast(
      () =>
        create.mutateAsync({
          name: values.name,
          linkedinAccountId: values.linkedinAccountId,
        }),
      {
        success: { title: t('create.createdToast') },
        onSuccess: (result) => onCreated(result.business.id),
        error: { title: t('create.createErrorToast') },
      },
    );

  // Load failed (the accounts query uses `retry: false`, so a single failure
  // sticks). Surface a retry path instead of falling through to the
  // "no account connected" banner below — which would be wrong when accounts
  // exist but the fetch errored.
  if (accounts.isError) {
    return (
      <Banner
        tone="error"
        data-testid="business-create-account-error"
        action={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => accounts.refetch()}
            data-testid="business-create-account-retry"
          >
            {t('create.accountRetry')}
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          <strong>{t('create.accountErrorTitle')}</strong>
          <span>{t('create.accountErrorBody')}</span>
        </div>
      </Banner>
    );
  }

  if (!accounts.isLoading && accounts.data.length === 0) {
    return (
      <Banner tone="warning" data-testid="business-create-no-account">
        <div className="flex flex-col gap-2">
          <strong>{t('create.accountNoneTitle')}</strong>
          <span>{t('create.accountNoneBody')}</span>
        </div>
      </Banner>
    );
  }

  return (
    <form
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit(onSubmit)(event);
      }}
      data-testid="business-create-form"
      className="flex flex-col gap-4"
    >
      <FieldAssembly
        label={t('create.nameLabel')}
        required
        error={form.formState.errors.name ? t('create.nameLabel') : undefined}
        name="name"
      >
        <Input
          type="text"
          placeholder={t('create.namePlaceholder')}
          autoFocus
          data-testid="business-create-name"
          {...form.register('name')}
        />
      </FieldAssembly>

      <FieldAssembly
        label={t('create.accountLabel')}
        description={t('create.accountHint')}
        required
        error={
          form.formState.errors.linkedinAccountId ? t('create.accountRequiredError') : undefined
        }
        hint={
          accounts.data.length === 1 && accountValue === accounts.data[0]?.id
            ? t('create.accountSinglePreselected')
            : undefined
        }
      >
        <Controller
          control={form.control}
          name="linkedinAccountId"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <Select.Trigger
                aria-label={t('create.accountLabel')}
                data-testid="business-create-account"
                disabled={accounts.isLoading}
              >
                <Select.Value
                  placeholder={
                    accounts.isLoading ? t('create.accountLoading') : t('create.accountPlaceholder')
                  }
                />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content layer="modal">
                  {accounts.data.map((account) => (
                    <Select.Item key={account.id} value={account.id}>
                      {account.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select>
          )}
        />
      </FieldAssembly>

      {create.isError ? (
        <Banner tone="error" data-testid="business-create-error">
          {t('create.createErrorToast')}
        </Banner>
      ) : null}

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onCancel}
            data-testid="business-create-cancel"
          >
            {t('create.cancel')}
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={form.formState.isSubmitting || create.isPending}
          disabled={accounts.isLoading}
          data-testid="business-create-submit"
        >
          {t('create.submit')}
        </Button>
      </div>
    </form>
  );
}
